/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2020-01-17 14:45:24
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import {
  Page,
  Layout,
  Card,
  FormLayout,
  PageActions,
  Button,
  SkeletonDisplayText,
  SkeletonBodyText,
  Stack
} from '@shopify/polaris';
import { withI18n } from '@shopify/react-i18n';
import { Field, FieldArray, reduxForm, getFormValues, change, reset } from 'redux-form';

import { RenderTextField } from 'src/components/views/common/forms/render-textfield';
import { RenderSelectMultiple } from 'src/components/views/common/forms/render-select-multiple';
import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderChoice } from 'src/components/views/common/forms/render-choice';

import { routes } from 'src/routes';

import MainMenu from 'src/components/containers/main-menu';
import ConditionsList from 'src/components/views/merchandising/conditions-list';
import ProcessingUpdateBanner from 'src/components/views/common/processing-update-banner';
import componentWithSyncWrapper from 'src/components/containers/common/component-with-sync-wrapper';
import TypeFormEmbedded from 'src/components/views/common/typeform-embedded';
import ExceededPlanNotification from 'src/components/containers/common/exceeded-plan-notifications';
import RouteLeavingGuard from 'src/components/views/common/handle-leave/route-leaving-guard';

import { RANKING, PRODUCTS_MATCH } from 'src/constants/merchandising';
import { CONDUCTOR_FOR_PRODUCT_INVENTORY } from 'src/constants/merchandising';
import { isRequired, hasTag } from 'src/helpers/form-validation-helper';
import { prettyText, confirmLeavePage } from 'src/helpers';
import {
  validateDataBeforeSave,
  buildMerchandising,
  initialMerchandising,
  isRelatedToDateAttribute,
  isRelatedToTextValues
} from 'src/helpers/merchandising-helpers';
import * as FilterDataActions from 'actions/filter-data-actions';
import * as MerchandisingActions from 'src/stores/actions/merchandising-actions';
import * as checklistActions from 'actions/dashboard/checklist-actions';

import type { ComposedProps } from './rule.type';

const isRequiredValue = isRequired();

class MerchandisingRule extends Component<ComposedProps> {
  timeoutProcessingUpdateBanner = undefined;

  componentDidMount() {
    const { getFilterData, getMerchandisingRuleNames, getMerchandising } = this.props;
    const { ruleId } = this.props.match.params;

    // Call API get filter data to get collections
    getFilterData();

    // Call API get merchandising rule names
    getMerchandisingRuleNames();

    if (!!ruleId) {
      getMerchandising(ruleId);
    }

    // Handle when reload or leave page
    window.addEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillUnmount() {
    // Handle when reload or leave page
    window.removeEventListener('beforeunload', this.confirmLeavePage);
  }

  componentWillReceiveProps(nextProps) {
    const { saving, scheduleSyncIn, waitSyncAt } = nextProps;
    const { saving: savingPrev, getSyncProcessStatus, updateShowBanner } = this.props;

    // Check to show banner Processing update when change status/ import
    if (savingPrev && !saving) {
      if (waitSyncAt) {
        // Time from now to sync point
        let syncDuration = waitSyncAt - Math.floor(Date.now() / 1000);
        syncDuration = syncDuration > 0 ? syncDuration : 0;
        // Time to finish sync point
        syncDuration = Math.floor(syncDuration / 60) + scheduleSyncIn;

        updateShowBanner(syncDuration, true);
      }
      // Call sync process status
      getSyncProcessStatus();
    }
  }

  render() {
    const {
      dirty,
      loading,
      saving,
      handleSubmit,
      pristine,
      submitting,
      formValues, // input values on form
      collections, // list of collections
      productTypes,
      tags,
      name: ruleName,
      syncDuration,
      showScheduleSync,
      saveSuccess,
      selectedPlan,
      history,
      i18n
    } = this.props;

    const { ruleId } = this.props.match.params;
    const isEdit = !!ruleId;

    const formattedRanking = RANKING.map(function (value) {
      return {
        ...value,
        label: i18n.translate(value.label)
      };
    });
    const formattedProductsMatch = PRODUCTS_MATCH.map(function (value) {
      return {
        ...value,
        label: i18n.translate(value.label)
      };
    });

    return (
      <div className="BC-Page BC-Page__Notifications BC-Page__Merchandising-Rule">
        {/* Menu */}
        <MainMenu menuId="menu-merchandising-merchandising" />

        <Page
          title={
            !isEdit && ruleName === ''
              ? i18n.translate('AdminHTML.Merchandising.Rule.pageTitle')
              : ruleName
          }
          subtitle={i18n.translate('AdminHTML.Merchandising.Rule.pageSubtitle')}
          breadcrumbs={[
            {
              content: i18n.translate('AdminHTML.Merchandising.Common.ruleBasedMerchandising'),
              disabled: false,
              onAction: this.backToManagementPage
            }
          ]}
          primaryAction={
            <div className="BC-Page BC-Page__Button BC-Page__Button-HasBreadcrumb">
              <Stack>
                {!pristine && (
                  <Button disabled={submitting} onClick={this.backToManagementPage}>
                    {i18n.translate('AdminHTML.Common.Action.cancel')}
                  </Button>
                )}
                <Button
                  primary
                  onClick={handleSubmit(this.handleSave.bind(this))}
                  disabled={pristine || submitting}
                  loading={saving && !pristine}>
                  {i18n.translate('AdminHTML.Common.Action.save')}
                </Button>
              </Stack>
            </div>
          }>
          <form onSubmit={this.handleDefaultSubmit}>
            <Layout>
              <Layout.Section>
                <div className="BC-Container__Notifications">
                  {/* Sticky notification for exceeded plan */}
                  <ExceededPlanNotification />

                  {/* Processing update banner */}
                  {showScheduleSync ? (
                    <ProcessingUpdateBanner
                      syncDuration={syncDuration}
                      featureName="merchandising"
                    />
                  ) : (
                    ''
                  )}
                </div>
              </Layout.Section>
              <Layout.Section>
                <Card title={i18n.translate('AdminHTML.Merchandising.Rule.card1_Title')}>
                  <Card.Section>
                    {loading ? (
                      <FormLayout>
                        <SkeletonDisplayText size="small" />
                        <SkeletonBodyText lines={1} />
                        <SkeletonDisplayText size="small" />
                        <SkeletonBodyText lines={1} />
                        <SkeletonDisplayText size="small" />
                        <SkeletonBodyText lines={1} />
                      </FormLayout>
                    ) : (
                      <FormLayout>
                        <Field
                          name="name"
                          component={RenderTextField}
                          label={i18n.translate('AdminHTML.Merchandising.Common.ruleName')}
                          placeholder={i18n.translate(
                            'AdminHTML.Merchandising.Rule.card1_Field1_Placeholder'
                          )}
                          validate={[isRequiredValue, hasTag]}
                        />
                        <Field
                          name="collections"
                          component={RenderSelectMultiple}
                          label={i18n.translate('AdminHTML.Merchandising.Common.collections')}
                          options={collections}
                          placeholder={i18n.translate(
                            'AdminHTML.Merchandising.Rule.card1_Field2_Placeholder'
                          )}
                          textHelper={i18n.translate(
                            'AdminHTML.Merchandising.Rule.card1_Field2_TextHelper'
                          )}
                        />
                        <Field
                          name="ranking"
                          component={RenderSelect}
                          label={i18n.translate('AdminHTML.Merchandising.Common.ranking')}
                          options={formattedRanking}
                        />
                      </FormLayout>
                    )}
                  </Card.Section>
                </Card>
              </Layout.Section>
              <Layout.Section>
                <Card title={i18n.translate('AdminHTML.Merchandising.Rule.card2_Title')}>
                  <Card.Section>
                    {loading ? (
                      <FormLayout>
                        <SkeletonBodyText lines={5} />
                      </FormLayout>
                    ) : (
                      <FormLayout>
                        <div className="conditions">
                          <Field
                            name="disjunctive"
                            choices={formattedProductsMatch}
                            component={RenderChoice}
                            label={i18n.translate(
                              'AdminHTML.Merchandising.Rule.card2_Field1_Label'
                            )}
                          />

                          <FieldArray
                            name="conditions"
                            component={ConditionsList}
                            formValues={formValues}
                            changeProductAttribute={this.changeProductAttribute}
                            onChooseDate={this.onChooseDate}
                            validate={this.conditionsValidate}
                            productTypes={productTypes}
                            tags={tags}
                          />
                        </div>
                      </FormLayout>
                    )}

                    {!loading ? (
                      <div className="addNewCondition">
                        <Button onClick={this.addNewCondition}>
                          {i18n.translate('AdminHTML.Merchandising.Rule.buttonChildren')}
                        </Button>
                      </div>
                    ) : (
                      ''
                    )}
                  </Card.Section>
                </Card>
              </Layout.Section>
            </Layout>

            <PageActions
              primaryAction={{
                content: i18n.translate('AdminHTML.Common.Action.save'),
                onAction: handleSubmit(this.handleSave.bind(this)),
                disabled: pristine || submitting,
                loading: saving && !pristine
              }}
              secondaryActions={
                !pristine
                  ? [
                      {
                        content: i18n.translate('AdminHTML.Common.Action.cancel'),
                        onAction: this.backToManagementPage
                      }
                    ]
                  : []
              }
            />
          </form>
        </Page>
        {!_.isEmpty(selectedPlan) && (
          <TypeFormEmbedded type="merchandising" conditionShow={saveSuccess} />
        )}

        {/* Confirm before leaving */}
        <RouteLeavingGuard
          when={dirty}
          // Navigate function
          navigate={path => history.push(path)}
          shouldBlockNavigation={dirty}
        />
      </div>
    );
  }

  // Control only date input value
  // For other fields, they have self validation!
  conditionsValidate = conditions => {
    const { i18n } = this.props;
    let errorsArray = [];

    if (!conditions || !conditions.length) return;

    conditions.forEach(condition => {
      const { field, value } = condition;
      if (isRelatedToDateAttribute(field) && !value) {
        errorsArray.push({
          value: i18n.translate('AdminHTML.Merchandising.Rule.conditionsValidate_Error')
        });
      }
    });

    return errorsArray.length ? errorsArray : null;
  };

  backToManagementPage = () => {
    const { history } = this.props;
    history.push({
      pathname: routes.merchandising.view(),
      state: { fromPage: 'MerchandisingPage' }
    });
  };

  // Add new condition
  addNewCondition = () => {
    const { formValues, updateField } = this.props;
    const { conditions = [] } = formValues;
    let clonedConditions = _.cloneDeep(conditions);

    clonedConditions.push({ field: '', relation: '', value: '' });

    // Update suggestion form
    updateField('conditions', clonedConditions);
  };

  // Handle default form submit
  handleDefaultSubmit(e) {
    e.preventDefault();
  }

  async handleSave(data) {
    // Get ruleId from params
    const { ruleId } = this.props.match.params;

    // Get props
    const { ruleNames, createMerchandising, updateMerchandising, history, getChecklist, i18n } =
      this.props;

    // Pretty data if needed
    let prettyData = { ...data, name: prettyText(data.name) };

    const errors = validateDataBeforeSave(prettyData, ruleNames, i18n);

    // No errors
    if (!_.isEmpty(errors)) {
      // Error process if needed
      return;
    }

    // Build merchandising package
    const merchandising = buildMerchandising(prettyData);

    if (ruleId) {
      await updateMerchandising(ruleId, merchandising);
      await getChecklist();
    } else {
      const result = await createMerchandising(merchandising);
      if (result.type === 'CREATE_MERCHANDISING_SUCCESS') {
        const ruleId = _.get(result, 'payload.data.data.id', '');
        // Switch page
        if (ruleId) {
          history.push(routes.merchandising.edit(ruleId));
        } else {
          history.push(routes.filterTree.list());
        }
      }
    }
  }

  // When product attribute changed, force reset relation or value
  changeProductAttribute = (index, newAttribute, oldAttribute, initialValue) => {
    const { updateField } = this.props;

    // Reset condition relation and initial value value
    if (newAttribute === 'inventory') {
      updateField(`conditions[${index}].value`, initialValue);
      updateField(`conditions[${index}].relation`, CONDUCTOR_FOR_PRODUCT_INVENTORY[0].value);
    } else {
      updateField(`conditions[${index}].relation`, initialValue);
    }

    // Init value if previous value is not text (object for another product attributes)
    if (isRelatedToTextValues(newAttribute) && !isRelatedToTextValues(oldAttribute)) {
      updateField(`conditions[${index}].value`, '');
    }

    // Reset date value
    if (isRelatedToDateAttribute(newAttribute) || isRelatedToDateAttribute(oldAttribute)) {
      updateField(`conditions[${index}].value`, '');
    }
  };

  onChooseDate = (date, index) => {
    this.props.updateField(`conditions[${index}].value`, date);
  };

  // Handle confirm leave page
  confirmLeavePage = event => {
    const { dirty, i18n } = this.props;
    return confirmLeavePage(event, dirty, i18n);
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Update redux-form field
  updateField: (field, value) => dispatch(change(`MerchandisingRuleForm`, field, value)),

  // Get available collections
  getFilterData: () => dispatch(FilterDataActions.getFilterData(ownProps.i18n)),

  // Get merchandising rule names
  getMerchandisingRuleNames: () =>
    dispatch(MerchandisingActions.getMerchandisingRuleNames(ownProps.i18n)),

  // Create merchandising rule
  createMerchandising: merchandising =>
    dispatch(MerchandisingActions.createMerchandising(merchandising, ownProps.i18n)),

  // Get merchandising rule
  getMerchandising: ruleId =>
    dispatch(MerchandisingActions.getMerchandising(ruleId, ownProps.i18n)),

  // Update merchandising rule
  updateMerchandising: (ruleId, merchandising) =>
    dispatch(
      MerchandisingActions.updateMerchandising(ruleId, merchandising, undefined, ownProps.i18n)
    ),

  // Reset form
  resetForm: () => dispatch(reset('MerchandisingRuleForm')),

  getChecklist: () => dispatch(checklistActions.getChecklist(ownProps.i18n))
});

const mapStateToProps = (state, props) => {
  // Get merchandising rule in case of editing
  const merchandising = _.get(state.merchandising, 'merchandising', {});

  // Get form values
  const formValues = getFormValues('MerchandisingRuleForm')(state);

  return {
    formValues,
    initialValues: !_.isEmpty(merchandising)
      ? initialMerchandising(merchandising)
      : {
          name: '',
          collections: [],
          ranking: 'high',
          disjunctive: [false],
          conditions: [{ field: '', relation: '', value: '' }]
        },
    name: formValues && formValues.name ? formValues.name : '',
    loading: _.get(state.merchandising, 'loading', true),
    saving: _.get(state.merchandising, 'saving', false),
    saveSuccess: _.get(state.merchandising, 'saveSuccess', false),
    collections: _.get(state.filterData.filterData, 'collections', []),
    productTypes: _.get(state.filterData.filterData, 'productTypes', []),
    tags: _.get(state.filterData.filterData, 'tags', []),
    ruleNames: _.get(state.merchandising, 'ruleNames', []),
    scheduleSyncIn: _.get(state.merchandising, 'scheduleSyncIn', 0),
    waitSyncAt: _.get(state.merchandising, 'waitSyncAt', 0),
    selectedPlan: _.get(state.oauth.tenantApp, 'selectedPlan')
  };
};

const MerchandisingRuleForm = reduxForm({
  form: 'MerchandisingRuleForm', // a unique identifier for this form
  enableReinitialize: true
})(MerchandisingRule);

export default componentWithSyncWrapper(
  withI18n()(connect(mapStateToProps, mapDispatchToProps)(MerchandisingRuleForm)),
  'SyncMerchandise'
);
